import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { tap } from 'rxjs/operators';

import { DocumentsStudyFolderDTO } from '../../dtos/documents-study-folder.dto';
import { DOCUMENT_CARD_STATE_TOKEN } from './document-card.token';
import { DocumentCardService } from '../../services/document-card.service';
import { CreateDocumentCardFolderAction, DeleteDocumentCardFolderAction, GetDocumentCardDetailsAction, GetDocumentCardRootFolderAction, GetDocumentCardSubFolderAction, ResetDocumentCardDetailsAction, ResetDocumentCardFoldersAction, UpdateDocumentCardFolderAction, UpdateDocumentFolderAction } from './document-card.action';
import { Observable } from 'rxjs';

export class IDocumentsStudyFolderState {
    documentsStudyFolder: DocumentsStudyFolderDTO[];
    folderDetails: DocumentsStudyFolderDTO;
}

@State({
    name: DOCUMENT_CARD_STATE_TOKEN,
    defaults: DocumentCardState.defaultState,
})
@Injectable()
export class DocumentCardState {
    static defaultState: IDocumentsStudyFolderState = {
        documentsStudyFolder: [],
        folderDetails: null,
    };

    constructor(
        private readonly store: Store,
        private documentCardService: DocumentCardService,
    ) { }

    @Selector()
    static getDocumentStudyFolder(state: IDocumentsStudyFolderState) {
        return state.documentsStudyFolder;
    }

    @Selector()
    static getFolderDetails(state: IDocumentsStudyFolderState) {
        return state.folderDetails;
    }

    @Action(GetDocumentCardRootFolderAction)
    getDocumentCardRootFolder(
        { patchState }: StateContext<IDocumentsStudyFolderState>,
        { organizationId, studyId }: GetDocumentCardRootFolderAction,
    ) {
        return this.documentCardService
            .getDocumentCardRootFolder(organizationId, studyId)
            .pipe(
                tap((folders) => {
                    patchState({
                        documentsStudyFolder: folders,
                    });
                }),
            );
    }

    @Action(GetDocumentCardSubFolderAction)
    getDocumentCardSubFolder(
        { patchState }: StateContext<IDocumentsStudyFolderState>,
        { organizationId, studyId, folderId }: GetDocumentCardSubFolderAction,
    ) {
        return this.documentCardService
            .getDocumentCardSubFolder(organizationId, studyId, folderId)
            .pipe(
                tap((folders) => {
                    patchState({
                        documentsStudyFolder: folders,
                    });
                }),
            );
    }

    @Action(CreateDocumentCardFolderAction)
    createDocumentCardFolder(
        { patchState }: StateContext<IDocumentsStudyFolderState>,
        { newFolder }: CreateDocumentCardFolderAction
    ): Observable<DocumentsStudyFolderDTO[]> {
        return this.documentCardService.createDocumentCardFolder(
            newFolder
        ).pipe(
            tap((createdFolder: DocumentsStudyFolderDTO[]) => {
                patchState({
                    documentsStudyFolder: createdFolder,
                });
            })
        );
    }

    @Action(UpdateDocumentCardFolderAction)
    updateDocumentCardFolder(
        { patchState }: StateContext<IDocumentsStudyFolderState>,
        { updatedFolder }: UpdateDocumentCardFolderAction
    ): Observable<DocumentsStudyFolderDTO[]> {
        return this.documentCardService.updateDocumentCardFolder(
            updatedFolder
        ).pipe(
            tap((updatedFolderResponse: DocumentsStudyFolderDTO[]) => {
                patchState({
                    documentsStudyFolder: updatedFolderResponse,
                });
            })
        );
    }

    @Action(DeleteDocumentCardFolderAction)
    deleteDocumentCardFolder(
        _ctx: StateContext<IDocumentsStudyFolderState>,
        { folderToDelete }: DeleteDocumentCardFolderAction
    ): Observable<DocumentsStudyFolderDTO[]> {
        return this.documentCardService.deleteDocumentCardFolder(folderToDelete);
    }

    @Action(GetDocumentCardDetailsAction)
    getDocumentCardDetails(
        { patchState }: StateContext<IDocumentsStudyFolderState>,
        { organizationId, studyId, folderId }: GetDocumentCardDetailsAction,
    ) {
        return this.documentCardService
            .getDocumentCardDetails(organizationId, studyId, folderId)
            .pipe(
                tap((folderDetails) => {
                    patchState({
                        folderDetails: folderDetails,
                    });
                }),
            );
    }

    @Action(ResetDocumentCardFoldersAction)
    resetFolders({ patchState }: StateContext<IDocumentsStudyFolderState>): void {
        patchState({
            documentsStudyFolder: [],
        });
    }

    @Action(ResetDocumentCardDetailsAction)
    resetFolderDetails({ patchState }: StateContext<IDocumentsStudyFolderState>): void {
        patchState({
            folderDetails: null,
        });
    }

    @Action(UpdateDocumentFolderAction)
    updateDocumentFolder(
        ctx: StateContext<IDocumentsStudyFolderState>,
        { organizationId, studyId, documentId, versionNo, folderId }: UpdateDocumentFolderAction
    ): Observable<void> {
        return this.documentCardService.updateDocumentFolder(
            organizationId,
            studyId,
            documentId,
            versionNo,
            folderId
        );
    }
}